import { Component, type ReactElement } from "react";
import { connect } from "react-redux";
import { type To } from "react-router";
import { Icons, LoadingSpinner, SymbolIcon } from "widgets";

import { ContentPlaceholder } from "@/ui-modules/core/ContentPlaceholder.tsx";

import { pushRoute } from "../../app-utils/route.js";
import {
  type Group,
  type Parent,
  type PlaybookEvent,
  type ProjectFile,
  type RecentItem,
  type RegionFlag,
  type View,
} from "../../graphql.js";
import { isDatasetFile } from "../../modules/files/utils/file.js";
import {
  type IDashboardCardRecentActivitiesList,
  loadDashboardCardRecentActivities,
} from "../../redux/modules/dashboard.js";
import {
  getFlagRoute,
  getGroupRoute,
  getParentRoute,
  getPlaybookEventRoute,
  getViewRoute,
} from "../../redux/modules/saved-view.js";
import { type IAppState } from "../../redux/reducer.js";
import { CONTENT_TYPES, FEATURE_DISPLAY_NAME, FILE_BROWSE_TYPES, ITEMS_TYPE, VIEW } from "../../settings/settings.js";
import { notify } from "@/modules/notifications/notify.tsx";

import { GenericTile } from "../core/DashboardTiles.js";
import { FileIcon } from "../core/FileIcon.js";
import GenericCard, { type IGenericCardProps } from "../core/GenericCard.js";
import { checkProjectFileExists, downloadProjectFile } from "@selector/shared/jsx";
import { selMessage } from "@selector/shared";

export interface IRecentActivitiesCardConfig {
  sorting: any;
}

type IRecentActivitiesDashboardCardOwnProps = IGenericCardProps;

interface IRecentActivitiesDashboardCardAppStateProps {
  recentActivities: IDashboardCardRecentActivitiesList;
}

interface IRecentActivitiesDashboardCardDispatchProps {
  loadRecentActivities: () => Promise<RecentItem[]>;
  downloadProjectFile: (projectFile: ProjectFile) => void;
}

export interface IRecentActivitiesDashboardCardProps
  extends
    IRecentActivitiesDashboardCardAppStateProps,
    IRecentActivitiesDashboardCardDispatchProps,
    IRecentActivitiesDashboardCardOwnProps {}

function mapStateToProps(
  state: IAppState,
  ownProps: IRecentActivitiesDashboardCardOwnProps,
): IRecentActivitiesDashboardCardAppStateProps {
  return {
    recentActivities: state.dashboardState.recentActivitiesCards[ownProps.id],
  };
}

function mapDispatchToProps(
  dispatch,
  ownProps: IRecentActivitiesDashboardCardOwnProps,
): IRecentActivitiesDashboardCardDispatchProps {
  return {
    loadRecentActivities: () =>
      dispatch(
        loadDashboardCardRecentActivities(
          ownProps.id,
          ownProps.config && (ownProps.config.specificConfig as IRecentActivitiesCardConfig),
        ),
      ),
    downloadProjectFile: (projectFile: ProjectFile) =>
      void checkProjectFileExists(projectFile.id).then((exists) => {
        if (exists) {
          downloadProjectFile(projectFile);
        } else {
          notify.warning(
            {
              title: selMessage({
                id: "file.download.failed.title",
              }),
              children: selMessage({
                id: "file.download.failed.children",
                values: { fileNames: projectFile.fileName, filesCount: 1 },
              }),
            },
            { autoClose: 5000 },
          );
        }
      }),
  };
}

class RecentActivitiesDashboardCard extends Component<IRecentActivitiesDashboardCardProps> {
  constructor(props: IRecentActivitiesDashboardCardProps) {
    super(props);
  }

  componentDidMount() {
    this.props.loadRecentActivities();
  }

  buildRecentActivityLink = (recentActivity: RecentItem): To => {
    switch (recentActivity.type) {
      case ITEMS_TYPE.SEARCH_RESULT:
        return `/search/${recentActivity.id}`;
      case ITEMS_TYPE.VIEW:
        return getViewRoute({ params: recentActivity.path, id: recentActivity.id } as View);
      case ITEMS_TYPE.REGION_FLAG:
        return getFlagRoute({ id: recentActivity.id } as RegionFlag);
      case ITEMS_TYPE.SAMPLE_PARENT:
        return getParentRoute({ id: recentActivity.id } as Parent);
      case ITEMS_TYPE.GROUP:
        return getGroupRoute({ id: recentActivity.id } as Group);
      case ITEMS_TYPE.EVENT:
        return getPlaybookEventRoute({ id: recentActivity.id } as PlaybookEvent);
      default:
        return null;
    }
  };

  buildRecentActivityOnClick = (recentActivity: RecentItem): ((any) => any) => {
    switch (recentActivity.type) {
      case ITEMS_TYPE.FILE:
        if (isDatasetFile({ fileName: recentActivity.name, fileBrowseType: FILE_BROWSE_TYPES.REGULAR_FILE })) {
          return () => pushRoute(`/visualizer/data/dataset/${recentActivity.id}`);
        } else if (Number(recentActivity.subType) === CONTENT_TYPES.LINK) {
          return () => window.open(recentActivity.url, "_blank");
        } else {
          return () => this.props.downloadProjectFile({ id: recentActivity.id } as ProjectFile);
        }
      default:
        return null;
    }
  };

  buildRecentActivityTypeLabel = (recentActivity: RecentItem): string => {
    switch (recentActivity.type) {
      case ITEMS_TYPE.SAMPLE_PARENT:
        return recentActivity.subType;
      case ITEMS_TYPE.FILE:
        // types are incompatible but this does not matter. We just need to be sure it is not a hyperlink
        if (isDatasetFile({ fileName: recentActivity.name, fileBrowseType: FILE_BROWSE_TYPES.REGULAR_FILE })) {
          return `Dataset`;
        } else if (Number(recentActivity.subType) === CONTENT_TYPES.LINK) {
          return `Project Link`;
        } else {
          return `Project File`;
        }
      case ITEMS_TYPE.VIEW:
        return VIEW[recentActivity.subType]?.title || "View";
      case ITEMS_TYPE.GROUP:
        return FEATURE_DISPLAY_NAME[recentActivity.subType]
          ? `${FEATURE_DISPLAY_NAME[recentActivity.subType]} group`
          : "Group";
      case ITEMS_TYPE.REGION_FLAG:
        return FEATURE_DISPLAY_NAME[recentActivity.type];
      case ITEMS_TYPE.SEARCH_RESULT:
        return `${recentActivity.subType} search`;
      case ITEMS_TYPE.EVENT:
        return `Playbook Event`;
      default:
        return recentActivity.type;
    }
  };

  getIconElement(recentActivity: RecentItem): ReactElement {
    switch (recentActivity.type) {
      case ITEMS_TYPE.SAMPLE_PARENT:
        return <img src={Icons.Tiles.SampleParent} width={24} height={30} alt={"Sample Parent"} />;
      case ITEMS_TYPE.SEARCH_RESULT:
        return <SymbolIcon icon={"search"} style={{ fontSize: 24 }} />;
      case ITEMS_TYPE.FILE:
        // types are incompatible but this does not matter. We just need to be sure it is not a hyperlink
        if (isDatasetFile({ fileName: recentActivity.name, fileBrowseType: recentActivity.type })) {
          return <img src={Icons.Tiles.AnalyticalDataViewer} width={24} height={30} alt={"Saved View"} />;
        } else if (Number(recentActivity.subType) === CONTENT_TYPES.LINK) {
          return <SymbolIcon icon={"link"} style={{ fontSize: 24 }} />;
        } else {
          return <FileIcon filename={recentActivity.name} size={"big"} />;
        }
      case ITEMS_TYPE.VIEW:
        return <img src={Icons.Tiles.SavedView} width={24} height={30} alt={"Saved View"} />;
      case ITEMS_TYPE.GROUP:
        return <img src={Icons.Tiles.SavedGroup} width={24} height={30} alt={"Saved Group"} />;
      case ITEMS_TYPE.REGION_FLAG:
        return <SymbolIcon icon={"flag"} style={{ fontSize: 24 }} />;
      case ITEMS_TYPE.EVENT:
        return <img src={Icons.Tiles.Events} width={24} height={30} alt={"Playbook Event"} />;
      default:
        return null;
    }
  }

  render(): ReactElement {
    const { recentActivities } = this.props;

    return (
      <GenericCard {...this.props} refreshData={this.props.loadRecentActivities} tipTourId={"recent-activities-title"}>
        {recentActivities && recentActivities.isLoading && <LoadingSpinner inCenter />}

        {recentActivities && !recentActivities.isLoading && recentActivities.recentActivities && (
          <div className="tiles_grid tiles_grid--projects">
            {recentActivities.recentActivities.length === 0 ? (
              <ContentPlaceholder>No recent activity</ContentPlaceholder>
            ) : null}
            {recentActivities.recentActivities.map((recentActivity) => (
              <GenericTile
                key={recentActivity.id}
                title={recentActivity.name}
                description={recentActivity.description}
                type={this.buildRecentActivityTypeLabel(recentActivity)}
                link={this.buildRecentActivityLink(recentActivity)}
                icon={this.getIconElement(recentActivity)}
                timeDetails={{
                  modificationDate: recentActivity.created,
                }}
                onClick={this.buildRecentActivityOnClick(recentActivity)}
              />
            ))}
          </div>
        )}
      </GenericCard>
    );
  }
}

export default connect<
  IRecentActivitiesDashboardCardAppStateProps,
  IRecentActivitiesDashboardCardDispatchProps,
  IRecentActivitiesDashboardCardOwnProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(RecentActivitiesDashboardCard);
